import { Injectable, Injector } from "@angular/core";
import { DocumentData } from "@angular/fire/compat/firestore";
import { doc, docSnapshots, Firestore, getDoc, onSnapshot, QuerySnapshot } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { CatchErrorService } from "@core/catch-error.service";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { catchError, defer, delay, from, map, Observable, of, retryWhen, scan, shareReplay, switchMap, take, throwError } from "rxjs";
import { CommonVariable } from "src/app/app.constants";
import { AppCheckService } from "../app-check.service";
import { BaseService } from "../app.base.service";
import { AuthStateService } from "@core/auth-state.service";

@Injectable({
  providedIn: "root",
})
export class FirestoreDataService extends BaseService {
  public static readonly FIREBASE_REGION = "europe-west3";


  /**
   * Gate every Firestore operation on customTokenReady$ to prevent
   * "Missing or insufficient permissions" errors caused by the race condition
   * where onSnapshot/getDoc fires before the Firebase custom token is available.
   */
  executeFirestoreOperation(url: string, method: string): Observable<any> {
    if (method === 'get') {
      return this.authStateService.customTokenReady$.pipe(
        take(1),
        switchMap(() => {
          const docRef = doc(this.firestore, url);
          return from(getDoc(docRef)).pipe(
            this.catchService.retryWhen,
            this.catchService.catchOfflineError
          );
        })
      );
    }

    if (method === 'snapshotChanges') {
      return this.authStateService.customTokenReady$.pipe(
        take(1),
        switchMap(() => {
          const docRef = doc(this.firestore, url);
          return new Observable((observer) => {
            const unsubscribe = onSnapshot(
              docRef,
              (docSnapshot) => observer.next(docSnapshot),
              (error) => observer.error(error)
            );

            // Cleanup
            return () => unsubscribe();
          }).pipe(
            this.catchService.retryWhen,
            this.catchService.catchOfflineError
          );
        })
      );
    }

    return of(null);
  }


  private async getAppCheckToken(): Promise<boolean> {
    const appCheckService = this.injector.get(AppCheckService);
    if (appCheckService && appCheckService.getToken) {
      try {
        const token = await appCheckService.getToken(true);
        return !!token;
      } catch (error) {
        console.error('Failed to get AppCheck token via helper:', error);
        return false;
      }
    }
    console.warn('AppCheckService or getToken method not available.');
    return false;
  }

  constructor(
    private firestore: Firestore,
    private catchService: CatchErrorService,
    private injector: Injector,
    private authStateService: AuthStateService,
  ) {
    super(injector);
  }

  /**
   * It takes an array of DocumentChangeAction objects and returns an array of objects with the id and
   * data properties
   * @param {DocumentChangeAction<unknown>[]} actions -
   * import("@angular/fire/compat/firestore").DocumentChangeAction<unknown>[]
   * @returns An array of objects.
   */
  public snapShotData(snapshot: QuerySnapshot<DocumentData>): Data[] {
    // Add type guard to ensure snapshot.docs is valid before mapping
    if (!snapshot || !snapshot.docs || !Array.isArray(snapshot.docs)) {
      console.warn('snapShotData: Invalid snapshot provided, returning empty array');
      return [];
    }
    
    return snapshot.docs.map((doc) => {
      const data = doc.data() as Data; // Fetch document data
      const id = doc.id; // Fetch document ID
      return { id, ...data }; // Combine ID with data
    });
  }
  
  processSnapshotData(data: DocumentData[]): any {
    // Add type guard to ensure data is an array before mapping
    if (!Array.isArray(data)) {
      console.warn('processSnapshotData: Input is not an array, returning empty array', { inputType: typeof data });
      return [];
    }
    
    // Process data as needed
    return data.map(doc => ({ id: doc.id, ...doc })); // Example mapping
  }
  // Get data from Angular firestore
  getAngularFireStore(url: string, method: string) {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          return of({
            data: () => null,
            payload: {
              data: () => null,
            },
          });
        }

        const docRef = doc(this.firestore, url); // Firestore document reference
        const needsAuthGate = typeof url === "string" && url.includes("users/");

        const runOp = () => {
          if (method === "get") {
            // Fetch the document using Firestore's getDoc()
            return from(getDoc(docRef)).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
          }

          if (method === "snapshotChanges") {
            // Fetch real-time updates using Firestore's docSnapshots()
            return docSnapshots(docRef).pipe(
              this.catchService.retryWhen,
              this.catchService.catchOfflineError,
              catchError((error) => {
                this.errorHandlerLogs('Error in getAngularFireStore (snapshotChanges):', error);
                // Return a mock DocumentSnapshot-like object to prevent downstream errors
                return of({
                  data: () => null,
                  exists: () => false,
                  id: 'error-placeholder',
                  payload: {
                    data: () => null,
                  },
                });
              })
            );
          }

          return of(null); // Return null if method is invalid
        };

        // Gate user-private paths only (LOTTOSOCIAL-PWA-1MEE); public paths unchanged.
        // Prefer executeFirestoreOperation for new code (always gated).
        return needsAuthGate
          ? this.authStateService.customTokenReady$.pipe(take(1), switchMap(() => runOp()))
          : runOp();
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      catchError((error) => {
        this.errorHandlerLogs('Error in getAngularFireStore:', error);
        // Return a mock DocumentSnapshot-like object to prevent downstream errors
        return of({
          data: () => null,
          exists: () => false,
          id: 'error-placeholder',
          payload: {
            data: () => null,
          },
        });
      }),
      shareReplay(1) // Cache the observable for multiple subscribers
    );
  }
  // Track if we have a valid AppCheck token
  private hasValidAppCheckToken = false;

  public getV2CloudFunction(CloudFunctionName: string, payload: Data | string, options: Data = {}) {
    try {
      const functions = getFunctions();
      functions.region = FirestoreDataService.FIREBASE_REGION;

      // Set timeout based on whether we have a valid AppCheck token - shorter timeout when no valid token
      const timeout = this.hasValidAppCheckToken ? 60000 : 30000;
      options = { ...options, timeout };

      return defer(() => {
        return from(httpsCallable(functions, CloudFunctionName, options)(payload));
      }).pipe(
        map((res: Data) => {
          // Successfully called function, so we have a valid token
          this.hasValidAppCheckToken = true;
          return res?.data;
        }),
        retryWhen((errors) =>
          errors.pipe(
            scan((retryCount, error) => {
              if (error?.code === 'permission-denied' || retryCount >= 3) {
                throw error;
              }
              return retryCount + 1;
            }, 0),
            delay(1000) // Delay for retry in milliseconds
          )
        ),
        catchError((error) => {
          return throwError(new Error(`Error in ${CloudFunctionName}, Reason: ${error}`));
        })
      );
    } catch (error) {
      return throwError(error);
    }
  }

  public v2CloudFunctionResponse(CFCall, checkUserAuth: boolean = true) {
    try {
      if (checkUserAuth) {
        const auth = getAuth();
        const user = auth?.currentUser;
        if (!user?.uid) {
          console.warn('User not authenticated in v2CloudFunctionResponse');
          return of({ response: { status: 'FAIL', message: 'User not authenticated' } });
        }
      }

      return from(CFCall).pipe(
        map((res: Data) => {
          // If we got a successful response, we have a valid token
          if (res) {
            this.hasValidAppCheckToken = true;
          }
          return res?.data;
        }),
        catchError((error) => {
          // Check if this was an AppCheck-related error
          if (error && (error?.code === 'permission-denied' || error?.code === 'appCheck/recaptcha-error')) {
            console.warn('AppCheck validation failed in cloud function call:', error);
            this.hasValidAppCheckToken = false;
          }
          console.error('Error in v2CloudFunctionResponse:', error);
          return of({ response: { status: 'FAIL', message: error?.message || 'Cloud function error' } });
        })
      );
    } catch (error) {
      console.error('Exception in v2CloudFunctionResponse:', error);
      return of({ response: { status: 'FAIL', message: error?.message || 'Unknown error' } });
    }
  }

  private EXPECTED_OS_VERSION = "17.1";

  // constructor(private authservice: AuthService) {}

  // callversionCF(userId: string) {
  //   try {
  //     const osversion = this.getOSVersion();
  //     if (osversion === this.EXPECTED_OS_VERSION) {
  //       setTimeout(() => {
  //         this.authservice.sendVersionMessage(userId);
  //       }, 3000);
  //     }
  //   } catch (error) {
  //     return "An error ocurred while checking OS version" + error.message;
  //   }
  // }

  errorHandlerLogs(message: string, error?: Data): { message: string; timestamp: string; error: Data } {
      const log = {
        message,
        timestamp: new Date().toISOString(),
        error: error ? this.formatError(error) : null,
      };
      return log;
  
  
    }
    // This method is used to format error messages
    private formatError(error: Data): Data {
      if (error instanceof Error) {
        return {
          name: error.name,
          message: error.message,
          stack: error.stack,
        };
      }
      return error;
    }

  getOSVersion() {
    const versionString = CommonVariable.device.osVersion;
    const operatingSys = CommonVariable.device.operatingSystem;
    if (operatingSys == "ios") {
      return versionString;
    }
  }

}
